// ============================================================
//  ADMIN — NEWS
//  Liste + Formular (anlegen/bearbeiten) in einer Seite.
// ============================================================
import { useEffect, useState } from "react";
import {
  fetchNews,
  createNews,
  updateNews,
  deleteNews,
  KATEGORIEN,
} from "../../cms/news";
import { fotoHochladen, fotoLoeschen } from "../../cms/upload";
import { useAuth } from "../../cms/AuthContext";

const LEER = {
  title: "", date: "", opponent: "", score: "", text: "",
  videoUrl: "", kategorie: "", autor: "", gepinnt: false,
};

export default function News() {
  const { user } = useAuth();
  const [liste, setListe] = useState([]);
  const [ladend, setLadend] = useState(true);
  const [formular, setFormular] = useState(null); // null = geschlossen, {} = neu, {...} = bearbeiten
  const [fotos, setFotos] = useState([]);
  const [hochladen, setHochladen] = useState(false);
  const [speichernLaeuft, setSpeichernLaeuft] = useState(false);
  const [fehler, setFehler] = useState("");

  async function neuLaden() {
    setLadend(true);
    setListe(await fetchNews());
    setLadend(false);
  }

  useEffect(() => { neuLaden(); }, []);

  function formularOeffnen(eintrag) {
    setFormular(eintrag ? { ...eintrag } : { ...LEER });
    setFotos(eintrag?.photoUrls || []);
    setFehler("");
  }

  async function fotoAuswahl(e) {
    const datei = e.target.files?.[0];
    if (!datei) return;
    setHochladen(true);
    setFehler("");
    try {
      const url = await fotoHochladen(datei, "matchberichte");
      setFotos((f) => [...f, url]);
    } catch (err) {
      setFehler(err.message);
    } finally {
      setHochladen(false);
      e.target.value = "";
    }
  }

  async function fotoEntfernen(url) {
    setFotos((f) => f.filter((u) => u !== url));
    await fotoLoeschen(url);
  }

  async function speichern(e) {
    e.preventDefault();
    setSpeichernLaeuft(true);
    setFehler("");
    try {
      const daten = { ...formular, photoUrls: fotos };
      if (formular.id) {
        const { id, ...rest } = daten;
        await updateNews(id, rest);
      } else {
        await createNews(daten, user.uid);
      }
      setFormular(null);
      await neuLaden();
    } catch (err) {
      setFehler(err.message);
    } finally {
      setSpeichernLaeuft(false);
    }
  }

  async function loeschen(id) {
    if (!window.confirm("Diesen News-Artikel wirklich löschen?")) return;
    await deleteNews(id);
    await neuLaden();
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-extrabold uppercase text-primary">News</h1>
        {!formular && (
          <button
            onClick={() => formularOeffnen(null)}
            className="bg-primary text-white font-bold uppercase text-sm px-5 py-2.5 hover:bg-accent transition-colors"
          >
            + Neu
          </button>
        )}
      </div>

      {formular && (
        <form onSubmit={speichern} className="border border-dark/10 p-6 mb-10 space-y-4">
          <h2 className="font-bold uppercase text-sm text-dark/60">
            {formular.id ? "Bearbeiten" : "Neuer News-Artikel"}
          </h2>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-semibold">Titel *</span>
              <input
                required
                value={formular.title}
                onChange={(e) => setFormular({ ...formular, title: e.target.value })}
                className="mt-1 w-full border border-dark/20 px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Datum *</span>
              <input
                required
                type="date"
                value={formular.date}
                onChange={(e) => setFormular({ ...formular, date: e.target.value })}
                className="mt-1 w-full border border-dark/20 px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Kategorie</span>
              <select
                value={formular.kategorie}
                onChange={(e) => setFormular({ ...formular, kategorie: e.target.value })}
                className="mt-1 w-full border border-dark/20 px-3 py-2 bg-white"
              >
                <option value="">— keine —</option>
                {KATEGORIEN.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Autor</span>
              <input
                value={formular.autor}
                onChange={(e) => setFormular({ ...formular, autor: e.target.value })}
                className="mt-1 w-full border border-dark/20 px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Gegner (optional, bei Matchberichten)</span>
              <input
                value={formular.opponent}
                onChange={(e) => setFormular({ ...formular, opponent: e.target.value })}
                className="mt-1 w-full border border-dark/20 px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Resultat</span>
              <input
                placeholder="3:1"
                value={formular.score}
                onChange={(e) => setFormular({ ...formular, score: e.target.value })}
                className="mt-1 w-full border border-dark/20 px-3 py-2"
              />
            </label>
          </div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formular.gepinnt}
              onChange={(e) => setFormular({ ...formular, gepinnt: e.target.checked })}
              className="h-4 w-4"
            />
            <span className="text-sm font-semibold">Als Hauptartikel anpinnen (oben auf der News-Seite)</span>
          </label>

          <label className="block">
            <span className="text-sm font-semibold">Text</span>
            <textarea
              rows={6}
              value={formular.text}
              onChange={(e) => setFormular({ ...formular, text: e.target.value })}
              className="mt-1 w-full border border-dark/20 px-3 py-2"
            />
          </label>

          <label className="block">
            <span className="text-sm font-semibold">YouTube-Link (Highlight-Video, optional)</span>
            <input
              placeholder="https://youtu.be/…"
              value={formular.videoUrl}
              onChange={(e) => setFormular({ ...formular, videoUrl: e.target.value })}
              className="mt-1 w-full border border-dark/20 px-3 py-2"
            />
          </label>

          <div>
            <span className="text-sm font-semibold">Fotos</span>
            <div className="mt-2 flex flex-wrap gap-3">
              {fotos.map((url) => (
                <div key={url} className="relative">
                  <img src={url} alt="" className="h-20 w-20 object-cover" />
                  <button
                    type="button"
                    onClick={() => fotoEntfernen(url)}
                    className="absolute -top-2 -right-2 bg-primary text-white h-6 w-6 text-xs font-bold rounded-full"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={fotoAuswahl}
              disabled={hochladen}
              className="mt-3 text-sm"
            />
            {hochladen && <p className="text-sm text-dark/50 mt-1">Lädt hoch …</p>}
          </div>

          {fehler && <p className="text-sm text-red-600">{fehler}</p>}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={speichernLaeuft}
              className="bg-primary text-white font-bold uppercase text-sm px-6 py-2.5 hover:bg-accent transition-colors disabled:opacity-50"
            >
              {speichernLaeuft ? "Speichert …" : "Speichern"}
            </button>
            <button
              type="button"
              onClick={() => setFormular(null)}
              className="font-semibold text-sm text-dark/60 hover:text-primary"
            >
              Abbrechen
            </button>
          </div>
        </form>
      )}

      {ladend ? (
        <p className="text-dark/50">Lädt …</p>
      ) : liste.length === 0 ? (
        <p className="text-dark/50">Noch keine News.</p>
      ) : (
        <ul className="divide-y divide-dark/10">
          {liste.map((m) => (
            <li key={m.id} className="py-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-bold flex items-center gap-1.5">
                  {m.gepinnt && (
                    <svg className="h-3.5 w-3.5 text-accent shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2a1 1 0 011 1v6.5l3.7 2.15a1 1 0 01-1 1.73L12 11 8.3 13.38a1 1 0 01-1-1.73L11 9.5V3a1 1 0 011-1z" />
                    </svg>
                  )}
                  {m.title}
                </p>
                <p className="text-sm text-dark/50">
                  {m.date} {m.kategorie && `— ${m.kategorie}`} {m.opponent && `— vs ${m.opponent}`} {m.score}
                </p>
              </div>
              <div className="flex gap-3 shrink-0">
                <button onClick={() => formularOeffnen(m)} className="text-sm font-semibold text-primary hover:text-accent">
                  Bearbeiten
                </button>
                <button onClick={() => loeschen(m.id)} className="text-sm font-semibold text-red-600 hover:text-red-800">
                  Löschen
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
